import { useTranslation } from "react-i18next";
import { FilePdf } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import {
  COMPANY_PAYMENT_REQUEST_STATUS_CONFIG,
  COMPANY_PAYMENT_REQUEST_TYPE_LABEL,
} from "@/lib/status-configs";
import type { CompanyPaymentRequest } from "@/types/company-billing";
import { formatInstantDate } from "./use-supervisor-billing";

export function PaymentRequestHistory({
  requests,
  isLoading,
}: {
  requests: CompanyPaymentRequest[];
  isLoading: boolean;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <div className="mx-5 my-4 h-16 animate-pulse rounded-lg bg-muted/40" />;
  }

  if (requests.length === 0) {
    return <p className="px-5 py-6 text-center text-sm text-muted-foreground">{t("supervisor.billing.requests.empty")}</p>;
  }

  return (
    <ul className="divide-y divide-border/30">
      {requests.map((r) => {
        const cfg = COMPANY_PAYMENT_REQUEST_STATUS_CONFIG[r.status];
        return (
          <li key={r.id} className="flex flex-col gap-2 px-5 py-3.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm font-medium text-foreground">
                {COMPANY_PAYMENT_REQUEST_TYPE_LABEL[r.type]}
                {r.amount !== null && (
                  <span className="ml-2 font-semibold tabular-nums">{formatCurrency(r.amount)}</span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatInstantDate(r.createdAt)}
                {r.requestedByName && ` · ${r.requestedByName}`}
              </p>
              {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
              {r.status === "rejected" && r.resolution && (
                <p className="text-xs text-destructive">
                  {t("supervisor.billing.requests.reason", { reason: r.resolution })}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {r.proformaNumber && (
                <a
                  href={`/api/company-payment-requests/${r.id}/proforma.pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <FilePdf className="size-3.5" />
                  {t("supervisor.billing.requests.proforma", { number: r.proformaNumber })}
                </a>
              )}
              <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold", cfg.bg, cfg.color)}>
                <span className={cn("size-1.5 rounded-full", cfg.dot)} />
                {cfg.label}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
